import { createKnob } from './knob.js';

function createSelect(state, path, options, label) {
  const control = document.createElement('div');
  control.className = 'panel-control';

  const select = document.createElement('select');
  for (const opt of options) {
    const option = document.createElement('option');
    option.value = opt.value;
    option.textContent = opt.label;
    select.appendChild(option);
  }

  select.addEventListener('change', () => {
    state.set(path, select.value);
  });

  state.subscribe(path, (val) => {
    select.value = val;
  });

  const labelEl = document.createElement('label');
  labelEl.textContent = label;

  control.appendChild(select);
  control.appendChild(labelEl);
  return control;
}

function createToggle(state, path, label) {
  const btn = document.createElement('button');
  btn.className = 'panel-toggle';
  btn.textContent = label;

  btn.addEventListener('click', () => {
    state.set(path, !state.get(path));
  });

  state.subscribe(path, (val) => {
    btn.classList.toggle('active', val);
  });

  return btn;
}

function createPanel(title, ...children) {
  const panel = document.createElement('div');
  panel.className = 'panel';

  const titleEl = document.createElement('div');
  titleEl.className = 'panel-title';
  titleEl.textContent = title;
  panel.appendChild(titleEl);

  const row = document.createElement('div');
  row.className = 'panel-row';
  for (const child of children) {
    row.appendChild(child);
  }
  panel.appendChild(row);
  return panel;
}

const oscTypes = [
  { value: 'sine', label: 'Sin' },
  { value: 'sawtooth', label: 'Saw' },
  { value: 'square', label: 'Sqr' },
  { value: 'triangle', label: 'Tri' },
];

const filterTypes = [
  { value: 'lowpass', label: 'LP' },
  { value: 'highpass', label: 'HP' },
  { value: 'bandpass', label: 'BP' },
];

const arpPatterns = [
  { value: 'up', label: 'Up' },
  { value: 'down', label: 'Down' },
  { value: 'up-down', label: 'Up/Dn' },
  { value: 'random', label: 'Rand' },
];

const arpRates = [
  { value: '4n', label: '1/4' },
  { value: '8n', label: '1/8' },
  { value: '16n', label: '1/16' },
  { value: '32n', label: '1/32' },
];

const lfoDests = [
  { value: 'filter', label: 'Filter' },
  { value: 'pitch', label: 'Pitch' },
  { value: 'amplitude', label: 'Amp' },
];

export function buildPanels(container, state) {
  // Oscillators
  const oscPanel = createPanel(
    'Oscillators',
    createSelect(state, 'osc1.type', oscTypes, 'OSC 1'),
    createSelect(state, 'osc2.type', oscTypes, 'OSC 2'),
    createKnob({ state, path: 'oscMix', label: 'Mix', min: 0, max: 1, step: 0.01 }),
    createKnob({ state, path: 'osc2.detune', label: 'Detune', min: -50, max: 50, step: 1,
      displayFn: (v) => `${Math.round(v)}ct` }),
    createToggle(state, 'fmEnabled', 'FM'),
    createKnob({ state, path: 'fmAmount', label: 'FM Amt', min: 0, max: 1000, step: 1,
      displayFn: (v) => Math.round(v) }),
  );

  // Filter
  const filterPanel = createPanel(
    'Filter',
    createKnob({ state, path: 'filter.cutoff', label: 'Cutoff', min: 20, max: 20000, step: 1,
      log: true, displayFn: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : Math.round(v) }),
    createKnob({ state, path: 'filter.resonance', label: 'Res', min: 0, max: 20, step: 0.1,
      displayFn: (v) => v.toFixed(1) }),
    createSelect(state, 'filter.type', filterTypes, 'Type'),
  );

  // Amplitude Envelope
  const envPanel = createPanel(
    'Envelope',
    createKnob({ state, path: 'ampEnv.attack', label: 'Atk', min: 0.001, max: 3, step: 0.001,
      log: true, displayFn: (v) => v >= 1 ? `${v.toFixed(1)}s` : `${Math.round(v * 1000)}ms` }),
    createKnob({ state, path: 'ampEnv.decay', label: 'Dec', min: 0.001, max: 3, step: 0.001,
      log: true, displayFn: (v) => v >= 1 ? `${v.toFixed(1)}s` : `${Math.round(v * 1000)}ms` }),
    createKnob({ state, path: 'ampEnv.sustain', label: 'Sus', min: 0, max: 1, step: 0.01 }),
    createKnob({ state, path: 'ampEnv.release', label: 'Rel', min: 0.01, max: 5, step: 0.01,
      log: true, displayFn: (v) => v >= 1 ? `${v.toFixed(1)}s` : `${Math.round(v * 1000)}ms` }),
  );

  // Effects
  const fxPanel = createPanel(
    'Effects',
    createKnob({ state, path: 'effects.reverb.wet', label: 'Reverb', min: 0, max: 1, step: 0.01 }),
    createKnob({ state, path: 'effects.delay.wet', label: 'Delay', min: 0, max: 1, step: 0.01 }),
    createKnob({ state, path: 'effects.delay.feedback', label: 'D.FB', min: 0, max: 0.9, step: 0.01 }),
    createKnob({ state, path: 'effects.chorus.wet', label: 'Chorus', min: 0, max: 1, step: 0.01 }),
    createKnob({ state, path: 'effects.distortion.wet', label: 'Dist', min: 0, max: 1, step: 0.01 }),
    createKnob({ state, path: 'effects.phaser.wet', label: 'Phaser', min: 0, max: 1, step: 0.01 }),
  );

  // Arpeggiator
  const arpPanel = createPanel(
    'Arpeggiator',
    createToggle(state, 'arp.enabled', 'On'),
    createSelect(state, 'arp.pattern', arpPatterns, 'Pattern'),
    createSelect(state, 'arp.rate', arpRates, 'Rate'),
    createKnob({ state, path: 'arp.octaves', label: 'Oct', min: 1, max: 4, step: 1,
      displayFn: (v) => Math.round(v) }),
  );

  // LFO
  const lfoPanel = createPanel(
    'LFO',
    createKnob({ state, path: 'lfo.rate', label: 'Rate', min: 0.05, max: 20, step: 0.01,
      log: true, displayFn: (v) => `${v.toFixed(1)}Hz` }),
    createKnob({ state, path: 'lfo.depth', label: 'Depth', min: 0, max: 1, step: 0.01 }),
    createSelect(state, 'lfo.destination', lfoDests, 'Dest'),
  );

  // Master
  const masterPanel = createPanel(
    'Master',
    createKnob({ state, path: 'master.volume', label: 'Volume', min: 0, max: 1, step: 0.01 }),
  );

  container.appendChild(oscPanel);
  container.appendChild(filterPanel);
  container.appendChild(envPanel);
  container.appendChild(fxPanel);
  container.appendChild(arpPanel);
  container.appendChild(lfoPanel);
  container.appendChild(masterPanel);
}
